import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMentorAuth } from '../hooks/MentorAuthContext';
import { toast } from 'react-toastify';

function MentorLogin() {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const { logIn, googleSignIn, githubSignIn } = useMentorAuth();
    const navigate = useNavigate();

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email.trim()) {
            newErrors.email = 'Email address is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast('Please check your email and password');
            return;
        }

        setLoading(true);

        try {
            await logIn(formData.email.trim(), formData.password);

            toast('Welcome back to Rtym!');
            navigate('/mentor/meet');
        } catch (err) {
            console.error('Login error:', err);

            if (err.status == 403) {
                toast("Please check your email to verify your account.", { icon: "🔒" });
            }
            else {
                let errorMessage = 'Login failed. Please try again.';
                if (err.code === 'auth/user-not-found') {
                    errorMessage = 'No account found with this email address. Please check your email or create a new account.';
                } else if (err.code === 'auth/wrong-password') {
                    errorMessage = 'Incorrect password. Please try again or reset your password.';
                } else if (err.code === 'auth/invalid-email') {
                    errorMessage = 'Please enter a valid email address.';
                } else if (err.code === 'auth/user-disabled') {
                    errorMessage = 'This account has been disabled. Please contact support.';
                } else if (err.code === 'auth/too-many-requests') {
                    errorMessage = 'Too many failed login attempts. Please try again later or reset your password.';
                } else if (err.code === 'auth/email-not-verified') {
                    errorMessage = 'Please verify your email address before signing in. Check your inbox for the verification link.';
                }
                toast(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSocialSignIn = async (provider) => {
        setLoading(true);

        try {
            if (provider === 'google') {
                await googleSignIn();
            } else if (provider === 'github') {
                await githubSignIn();
            }

            toast(`Welcome back! Signed in with ${provider}.`);
            navigate('/mentor/meet');
        } catch (err) {
            console.error(`${provider} sign-in error:`, err);

            if (err.status == 403) {
                toast("Please check your email to verify your account.", { icon: "🔒" });
            } else {


                if (err.message.includes('popup-closed-by-user')) {
                    toast('Sign-in was cancelled. Please try again.');
                }

                if (err.message.includes('account-exists-with-different-credential')) {
                    toast('An account with this email already exists. Please use a different sign-in method.');
                }

                let errorMessage = err.message || `Failed to sign in with ${provider}. Please try again.`;

                if (err.code === 'auth/popup-closed-by-user') {
                    errorMessage = 'Sign-in was cancelled. Please try again.';
                } else if (err.code === 'auth/account-exists-with-different-credential') {
                    errorMessage = 'An account with this email already exists. Please use a different sign-in method.';
                }

                toast(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            <div className="w-full lg:w-1/2 bg-white p-6 lg:p-8 flex flex-col justify-center overflow-auto">
                <div className="max-w-md mx-auto w-full">
                    <div className="mb-6">
                        <img
                            src="https://ik.imagekit.io/vituepzjm/logoipsum-361.svg?updatedAt=1755629800331"
                            alt="Rtym Logo"
                            className="h-10 mb-4"
                        />
                        <h1 className="text-2xl font-bold text-gray-900">Welcome Back</h1>
                        <p className="text-gray-600 text-sm">Sign in to access your mentor dashboard and continue guiding others</p>
                    </div>

                    <div className="grid grid-cols-1 gap-3 mb-4">
                        <button
                            onClick={() => handleSocialSignIn('google')}
                            disabled={loading}
                            className="flex items-center cursor-pointer justify-center gap-2 py-2 px-3 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <svg className="size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
                            </svg>
                            <span>Continue with Google</span>
                        </button>
                        <button
                            onClick={() => handleSocialSignIn('github')}
                            disabled={loading}
                            className="flex items-center cursor-pointer justify-center gap-2 py-2 px-3 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.757-1.333-1.757-1.089-.745.083-.729.083-.729 1.205.084 1.84 1.236 1.84 1.236 1.07 1.834 2.809 1.304 3.495.997.108-.775.418-1.305.762-1.605-2.665-.305-5.466-1.332-5.466-5.93 0-1.31.469-2.381 1.236-3.221-.124-.303-.535-1.523.117-3.176 0 0 1.008-.322 3.301 1.23a11.52 11.52 0 013.003-.404c1.018.005 2.045.138 3.003.404 2.291-1.553 3.297-1.23 3.297-1.23.653 1.653.242 2.873.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.61-2.803 5.624-5.475 5.921.43.372.823 1.102.823 2.222v3.293c0 .322.218.694.825.576C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                            </svg>
                            <span>Continue with GitHub</span>
                        </button>
                    </div>

                    <div className="relative mb-4">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-xs">
                            <span className="px-2 bg-white text-gray-500">Or sign in with email</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-3" noValidate>
                        <div>
                            <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1">
                                Email Address *
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={formData.email}
                                onChange={handleInputChange}
                                className={`w-full px-3 py-2 rounded-md border ${errors.email ? 'border-red-500' : 'border-gray-300'
                                    } focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent`}
                                placeholder="Enter your email address"
                            />
                            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-xs font-medium text-gray-700 mb-1">
                                Password *
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    required
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className={`w-full px-3 py-2 rounded-md border ${errors.password ? 'border-red-500' : 'border-gray-300'
                                        } focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent`}
                                    placeholder="Enter your password"
                                />
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? (
                                        // Eye open icon
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    ) : (
                                        // Eye closed icon
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.956 9.956 0 012.908-4.362M6.634 6.634A9.956 9.956 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.96 9.96 0 01-4.284 5.255M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                        </div>

                        <div className="flex items-center justify-end text-xs">
                            <Link to="/user/forgot-password" className="font-medium text-violet-800 hover:text-violet-900 underline">
                                Forgot password?
                            </Link>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-2 rounded-md text-white font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 ${loading
                                ? 'bg-violet-600 cursor-not-allowed'
                                : 'bg-violet-800 hover:bg-violet-900'
                                }`}
                        >
                            {loading ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Signing in...
                                </span>
                            ) : (
                                'Sign In to Dashboard'
                            )}
                        </button>
                    </form>

                    <p className="mt-4 text-center text-xs text-gray-500">
                        Don't have an account?{' '}
                        <Link to="/user/register" className="font-medium text-violet-800 hover:text-violet-900 underline">
                            Create your mentor account
                        </Link>
                    </p>

                    <div className="mt-3 text-xs text-center text-gray-500">
                        By signing in, you agree to our{' '}
                        <Link to="/privacy" className="text-violet-800 hover:text-violet-900 underline">Privacy Policy</Link>
                        {' '}and{' '}
                        <Link to="/terms" className="text-violet-800 hover:text-violet-900 underline">Terms of Service</Link>.
                    </div>
                </div>
            </div>

            <div className="hidden lg:block lg:w-1/2 bg-violet-900 text-white">
                <div className="h-full flex flex-col justify-between p-8">
                    <div className="flex-1 flex flex-col justify-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-500 rounded-full mb-6">
                            <svg className="w-8 h-8 text-white" stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="200px" width="200px" xmlns="http://www.w3.org/2000/svg"><path d="M9 8v-2a2 2 0 0 1 2 -2h7a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-7a2 2 0 0 1 -2 -2v-2"></path><path d="M3 12h13l-3 -3"></path><path d="M13 15l3 -3"></path></svg>
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold mb-4">
                            Connect with IT professionals
                        </h1>
                        <p className="text-lg text-violet-100 mb-6">
                            Get personalized career guidance through 1:1 consultations
                        </p>
                        <div className="space-y-3">
                            <div className="flex items-start">
                                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-violet-300 flex items-center justify-center">
                                    <svg className="h-3 w-3 text-violet-900" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <p className="ml-2 text-sm text-violet-100">Book 15-30 minute consultation calls</p>
                            </div>
                            <div className="flex items-start">
                                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-violet-300 flex items-center justify-center">
                                    <svg className="h-3 w-3 text-violet-900" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <p className="ml-2 text-sm text-violet-100">Practice with mock interviews</p>
                            </div>
                            <div className="flex items-start">
                                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-violet-300 flex items-center justify-center">
                                    <svg className="h-3 w-3 text-violet-900" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <p className="ml-2 text-sm text-violet-100">Get AI-powered resume feedback</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Loading Overlay */}
            {loading && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-50">
                    <div className="flex flex-col items-center space-y-4">
                        <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-white font-medium text-lg">Signing you in...</p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MentorLogin;